import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import excel from "exceljs";
import SnackBar from "components/SnackBar/SnackBar";
import {
  validateNoRosterIdExcel,
  getNoRosterOracleIdDetails,
  saveOMTClassDetails,
} from "services/userServices";

// ─── Modal States ─────────────────────────────────────────────────────────────
const MODAL_STATE = {
  INITIAL: "initial",
  UNIDENTIFIED_IDS: "unidentifiedIds",
  VALIDATION_SUMMARY: "validationSummary",
  SUCCESS: "success",
};

const REQUIRED_COLUMN = "Oracle ID";

const TABLE_SX = {
  border: "0.5px solid #e2e8f0",
  borderRadius: "8px",
  overflow: "hidden",
  maxHeight: 240,
  overflowY: "auto",
  mb: 1,
};

const TH_SX = {
  fontSize: "11px",
  fontWeight: 500,
  color: "#64748b",
  background: "#f1f5f9",
  py: "6px",
  px: "10px",
  borderBottom: "0.5px solid #e2e8f0",
  whiteSpace: "nowrap",
};

const TD_SX = {
  fontSize: "12px",
  py: "5px",
  px: "10px",
  borderBottom: "0.5px solid #f1f5f9",
};

const statusChip = (label, color) => (
  <Chip
    label={label}
    size="small"
    sx={{
      fontSize: "10px",
      height: 20,
      fontWeight: 500,
      bgcolor: color.bg,
      color: color.fg,
      border: `0.5px solid ${color.border}`,
      borderRadius: "10px",
    }}
  />
);

const CHIP_COLORS = {
  termed: { bg: "#fee2e2", fg: "#7f1d1d", border: "#fca5a5" },
  active: { bg: "#f0fdf4", fg: "#14532d", border: "#86efac" },
  inActive: { bg: "#f5f3ff", fg: "#4c1d95", border: "#c4b5fd" },
};

const TERMED_STATUSES = ["terminate assignment", "terminate - process"];

const ACTIVE_STATUSES = [
  "active assignment",
  "active contingent assignment",
  "active - payroll eligible",
  "active - no payroll",
  "active application",
  "active appointment",
  "accepted",
  "pending clearance",
];

const isTempInactive = (rawStatus) => {
  if (!rawStatus || rawStatus.trim() === "") return false;
  const s = rawStatus.toLowerCase().trim();
  return !TERMED_STATUSES.includes(s) && !ACTIVE_STATUSES.includes(s);
};

const getStatusChip = (rawStatus) => {
  if (!rawStatus || rawStatus.trim() === "") {
    return statusChip("Active • No status found", CHIP_COLORS.active);
  }
  const s = rawStatus.toLowerCase().trim();
  if (TERMED_STATUSES.includes(s))
    return statusChip("Termed", CHIP_COLORS.termed);
  if (ACTIVE_STATUSES.includes(s))
    return statusChip("Active", CHIP_COLORS.active);
  return statusChip("Temp Inactive", CHIP_COLORS.inActive);
};

const AlertBox = ({ type, children }) => {
  const config = {
    error: {
      bg: "#fdecea",
      border: "#f09595",
      color: "#a32d2d",
      Icon: ErrorOutlineIcon,
    },
    warning: {
      bg: "#fff8e1",
      border: "#fac775",
      color: "#633806",
      Icon: WarningAmberOutlinedIcon,
    },
    info: {
      bg: "#e6f1fb",
      border: "#b5d4f4",
      color: "#0c447c",
      Icon: InfoOutlinedIcon,
    },
    success: {
      bg: "#f0fdf4",
      border: "#86efac",
      color: "#27500A",
      Icon: CheckCircleOutlineIcon,
    },
  };
  const { bg, border, color, Icon } = config[type] || config.info;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        background: bg,
        border: `0.5px solid ${border}`,
        borderRadius: "8px",
        p: "10px 12px",
        mb: 1.5,
        color,
      }}
    >
      <Icon sx={{ fontSize: 18, mt: "1px", flexShrink: 0 }} />
      <Box sx={{ fontSize: "12px", lineHeight: 1.6 }}>{children}</Box>
    </Box>
  );
};

AlertBox.propTypes = {
  type: PropTypes.oneOf(["error", "warning", "info", "success"]).isRequired,
  children: PropTypes.node.isRequired,
};

const FilePill = ({ name, rowCount }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      background: "#f8fafc",
      border: "0.5px solid #e2e8f0",
      borderRadius: "8px",
      p: "7px 10px",
      mb: 1.5,
    }}
  >
    <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: "#1976d2" }} />
    <Typography sx={{ fontSize: "12px" }}>{name}</Typography>
    {rowCount != null && (
      <Typography sx={{ fontSize: "11px", color: "#6b7280", ml: "auto" }}>
        {rowCount} rows
      </Typography>
    )}
  </Box>
);

FilePill.propTypes = {
  name: PropTypes.string.isRequired,
  rowCount: PropTypes.number,
};

const SubNote = ({ children }) => (
  <Typography sx={{ fontSize: "11px", color: "#6b7280", mt: "4px", mb: "2px" }}>
    {children}
  </Typography>
);

SubNote.propTypes = { children: PropTypes.node.isRequired };

// ─── Main Component ───────────────────────────────────────────────────────────
const UploadClassModal = ({ open, onClose, onUploadSuccess }) => {
  const [modalState, setModalState] = useState(MODAL_STATE.INITIAL);
  const [loader, setLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [fileError, setFileError] = useState("");
  const [parsedOracleIds, setParsedOracleIds] = useState([]);
  const [unidentifiedIds, setUnidentifiedIds] = useState([]);
  const [validIds, setValidIds] = useState([]);
  const [duplicateInRecords, setDuplicateInRecords] = useState([]);
  const [cancelledScheduleIds, setCancelledScheduleIds] = useState([]);
  const [successInfo, setSuccessInfo] = useState(null);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const fileInputRef = useRef(null);

  const [terminatedEmployees, setTerminatedEmployees] = useState([]);
  const [tempInactiveEmployees, setTempInactiveEmployees] = useState([]);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [selectedScheduledIds, setSelectedScheduledIds] = useState(new Set());
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState(new Set());
  const [selectedTempInactiveIds, setSelectedTempInactiveIds] = useState(new Set());
  const [selectedActiveIds, setSelectedActiveIds] = useState(new Set());
  const pendingValidIdsRef = useRef([]);

  const resetModal = () => {
    setModalState(MODAL_STATE.INITIAL);
    setSelectedFile(null);
    setFileKey((k) => k + 1);
    setFileError("");
    setParsedOracleIds([]);
    setUnidentifiedIds([]);
    setValidIds([]);
    setDuplicateInRecords([]);
    setCancelledScheduleIds([]);
    setSuccessInfo(null);
    setLoader(false);
    setTerminatedEmployees([]);
    setTempInactiveEmployees([]);
    setActiveEmployees([]);
    setSelectedScheduledIds(new Set());
    setSelectedDuplicateIds(new Set());
    setSelectedTempInactiveIds(new Set());
    setSelectedActiveIds(new Set());
    pendingValidIdsRef.current = [];
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const workbook = new excel.Workbook();
      const sheet = workbook.addWorksheet("OMT Upload Template");
      sheet.columns = [{ header: "Oracle ID", key: "oracleId", width: 20 }];
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE3F2FD" },
      };
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "OMT_Upload_Template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnack({
        open: true,
        message: "Failed to download template.",
        severity: "error",
      });
    }
  };

  const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const workbook = new excel.Workbook();
          await workbook.xlsx.load(e.target.result);
          const sheet = workbook.worksheets[0];
          const headers = [];
          sheet
            .getRow(1)
            .eachCell((cell) => headers.push(cell.value?.toString().trim()));
          if (!headers.includes(REQUIRED_COLUMN)) {
            reject(
              `Invalid template. Required column "${REQUIRED_COLUMN}" not found. Found: ${headers.join(", ")}`,
            );
            return;
          }
          const oracleIdColIndex = headers.indexOf(REQUIRED_COLUMN) + 1;
          const ids = [];
          sheet.eachRow((row, rowNum) => {
            if (rowNum === 1) return;
            const val = row.getCell(oracleIdColIndex).value;
            if (val !== null && val !== undefined && val !== "")
              ids.push(val.toString().trim());
          });
          resolve(ids);
        } catch {
          reject(
            "Failed to parse file. Please ensure it is a valid .xlsx file.",
          );
        }
      };
      reader.onerror = () => reject("Failed to read file.");
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      setFileError("Please upload a valid .xlsx or .xls file.");
      setSelectedFile(null);
      return;
    }
    setFileError("");
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setFileError("Please select a file to upload.");
      return;
    }
    setLoader(true);
    try {
      let oracleIds;
      try {
        oracleIds = await parseExcelFile(selectedFile);
      } catch (err) {
        setFileError(typeof err === "string" ? err : "Failed to parse file.");
        setLoader(false);
        return;
      }
      if (!oracleIds || oracleIds.length === 0) {
        setFileError(
          "The uploaded file contains no data rows. Please add Oracle IDs and re-upload.",
        );
        setLoader(false);
        return;
      }
      const seen = new Set();
      const dupes = [];
      const deduped = [];
      oracleIds.forEach((id) => {
        if (seen.has(id)) {
          dupes.push(id);
        } else {
          seen.add(id);
          deduped.push(id);
        }
      });
      if (dupes.length > 0) {
        setSnack({
          open: true,
          message: `${[...new Set(dupes)].length} duplicate Oracle ID${[...new Set(dupes)].length > 1 ? "s" : ""} found and removed: ${[...new Set(dupes)].join(", ")}. Continuing with ${deduped.length} unique ID${deduped.length !== 1 ? "s" : ""}.`,
          severity: "warning",
        });
      }
      setParsedOracleIds(deduped);
      await proceedWithValidation(deduped);
    } catch {
      setSnack({
        open: true,
        message: "An unexpected error occurred. Please try again.",
        severity: "error",
      });
      setLoader(false);
    }
  };

  const proceedWithValidation = async (oracleIds) => {
    setLoader(true);
    try {
      const res = await validateNoRosterIdExcel(oracleIds);
      const rows = res?.Data || res?.data || [];
      const firstRow = Array.isArray(rows) ? rows[0] : rows;
      const isErrorVal =
        firstRow?.IsError ?? firstRow?.isError ?? firstRow?.iserror;
      const hasError = isErrorVal !== undefined && parseInt(isErrorVal) === 1;

      if (!firstRow || !hasError) {
        setValidIds(oracleIds);
        await proceedWithInsert(oracleIds);
        return;
      }

      const errorRows = Array.isArray(rows) ? rows : [rows];
      const getMsg = (r) =>
        (r.message || r.Message || r.MESSAGE || "").toLowerCase();

      const duplicateInFile = [
        ...new Set(
          errorRows
            .filter((r) =>
              getMsg(r).includes("duplicate oracleid exist in this excel"),
            )
            .map((r) => r.oracleId || r.OracleId || r.oracleid),
        ),
      ];
      const notFound = [
        ...new Set(
          errorRows
            .filter((r) => getMsg(r).includes("oracleid does not exist"))
            .map((r) => r.oracleId || r.OracleId || r.oracleid),
        ),
      ];

      const cancelledScheduleMap = {};
      errorRows
        .filter((r) => getMsg(r).includes("with cancelled"))
        .forEach((r) => {
          const oracleId = r.oracleId || r.OracleId || r.oracleid;
          if (!cancelledScheduleMap[oracleId]) {
            const msg = r.message || r.Message || r.MESSAGE || "";
            const classNameMatch = msg.match(
              /other named class\s+(.+?)\s+with cancelled/i,
            );
            const scheduleTypeMatch = msg.match(/with cancelled\s+(.+)/i);
            cancelledScheduleMap[oracleId] = {
              OracleId: oracleId,
              ClassName: classNameMatch
                ? classNameMatch[1].trim()
                : "Unknown class",
              ScheduleType: scheduleTypeMatch
                ? scheduleTypeMatch[1].trim()
                : "Schedule",
            };
          }
        });
      const cancelledSchedules = Object.values(cancelledScheduleMap);
      const cancelledOracleIds = cancelledSchedules.map((r) => r.OracleId);

      const duplicateInRecordsMap = {};
      errorRows
        .filter((r) => {
          const m = getMsg(r);
          return (
            (m.includes("oracleid exist in other named class") &&
              !m.includes("with cancelled")) ||
            m.includes("oracleid exist in other class roster")
          );
        })
        .forEach((r) => {
          const oracleId = r.oracleId || r.OracleId || r.oracleid;
          if (!duplicateInRecordsMap[oracleId]) {
            const msg = r.message || r.Message || r.MESSAGE || "";
            const namedClassMatch = msg.match(/other named class\s+(.+)/i);
            const classRosterMatch = msg.match(/other class roster\s+(.+)/i);
            const className = namedClassMatch
              ? namedClassMatch[1].trim()
              : classRosterMatch
                ? `Class Roster ${classRosterMatch[1].trim()}`
                : "Unknown class";
            duplicateInRecordsMap[oracleId] = {
              OracleId: oracleId,
              ClassName: className,
            };
          }
        });
      const duplicatesInRecords = Object.values(duplicateInRecordsMap);

      if (duplicateInFile.length > 0) {
        const filteredIds = oracleIds.filter(
          (id) => !duplicateInFile.includes(id),
        );
        setSnack({
          open: true,
          message: `${duplicateInFile.length} duplicate Oracle ID${duplicateInFile.length > 1 ? "s" : ""} found and removed: ${duplicateInFile.join(", ")}. Continuing with ${filteredIds.length} unique ID${filteredIds.length !== 1 ? "s" : ""}.`,
          severity: "warning",
        });
        setParsedOracleIds(filteredIds);
        if (filteredIds.length === 0) {
          setLoader(false);
          return;
        }
        await proceedWithValidation(filteredIds);
        return;
      }

      if (notFound.length > 0) {
        const validOracleIds = oracleIds.filter((id) => !notFound.includes(id));
        const dupOracleIds = [
          ...duplicatesInRecords.map((r) => r.OracleId),
          ...cancelledOracleIds,
        ];
        const trulyValidIds = validOracleIds.filter(
          (id) => !dupOracleIds.includes(id),
        );
        setUnidentifiedIds(notFound);
        setValidIds(trulyValidIds.length > 0 ? trulyValidIds : validOracleIds);
        if (duplicatesInRecords.length > 0)
          setDuplicateInRecords(duplicatesInRecords);
        if (cancelledSchedules.length > 0)
          setCancelledScheduleIds(cancelledSchedules);
        setModalState(MODAL_STATE.UNIDENTIFIED_IDS);
        setLoader(false);
        return;
      }

      if (duplicatesInRecords.length > 0) {
        setDuplicateInRecords(duplicatesInRecords);
        setSelectedDuplicateIds(
          new Set(duplicatesInRecords.map((r) => r.OracleId)),
        );
        if (cancelledSchedules.length > 0) {
          setCancelledScheduleIds(cancelledSchedules);
          setSelectedScheduledIds(
            new Set(cancelledSchedules.map((r) => r.OracleId)),
          );
        }
        const cleanIds = oracleIds.filter(
          (id) =>
            !duplicatesInRecords.map((r) => r.OracleId).includes(id) &&
            !cancelledOracleIds.includes(id),
        );
        setValidIds(cleanIds);
        await proceedWithInsert(
          cleanIds,
          [],
          duplicatesInRecords,
          cancelledSchedules,
        );
        return;
      }

      if (cancelledSchedules.length > 0) {
        setCancelledScheduleIds(cancelledSchedules);
        setSelectedScheduledIds(
          new Set(cancelledSchedules.map((r) => r.OracleId)),
        );
        const cleanIds = oracleIds.filter(
          (id) => !cancelledOracleIds.includes(id),
        );
        setValidIds(cleanIds);
        await proceedWithInsert(cleanIds, [], [], cancelledSchedules);
        return;
      }

      setValidIds(oracleIds);
      await proceedWithInsert(oracleIds);
    } catch {
      setSnack({
        open: true,
        message: "Validation failed. Please try again.",
        severity: "error",
      });
      setLoader(false);
    }
  };

  const handleSkipAndContinue = async () => {
    if (!validIds || validIds.length === 0) {
      setSnack({
        open: true,
        message:
          "No valid Oracle IDs to upload. Please correct the file and re-upload.",
        severity: "warning",
      });
      return;
    }
    if (duplicateInRecords.length > 0 || cancelledScheduleIds.length > 0) {
      await proceedWithInsert(validIds);
      return;
    }
    await proceedWithInsert(validIds);
  };

  const handleValidationSummaryConfirm = async () => {
    setLoader(true);
    try {
      const selectedDupes = duplicateInRecords.filter((r) =>
        selectedDuplicateIds.has(r.OracleId),
      );
      const selectedSched = cancelledScheduleIds.filter((r) =>
        selectedScheduledIds.has(r.OracleId),
      );
      const allExistingDuplicates = [...selectedDupes, ...selectedSched];
      const selectedDupeIds = allExistingDuplicates.map((r) => r.OracleId);

      const tempInactiveWithClass = tempInactiveEmployees
        .filter(
          (e) =>
            selectedTempInactiveIds.has(e.OracleId || e.oracleId) &&
            e.CurrentClassName,
        )
        .map((e) => ({
          OracleId: e.OracleId || e.oracleId,
          ClassName: e.CurrentClassName,
        }));

      const allRecordsWithClass = [
        ...allExistingDuplicates,
        ...tempInactiveWithClass,
      ];

      const finalIds = [
        ...new Set([
          ...Array.from(selectedActiveIds),
          ...selectedDupeIds,
          ...Array.from(selectedTempInactiveIds),
        ]),
      ];

      if (finalIds.length === 0) {
        setSnack({
          open: true,
          message: "No valid Oracle IDs to upload after applying selections.",
          severity: "warning",
        });
        setLoader(false);
        return;
      }

      const employeeDetails = await fetchEmployeeDetails(finalIds);
      if (!employeeDetails || employeeDetails.length === 0) {
        setSnack({
          open: true,
          message: "Failed to fetch employee details. Please try again.",
          severity: "error",
        });
        setLoader(false);
        return;
      }
      await saveEmployees(employeeDetails, allRecordsWithClass);
    } catch {
      setSnack({
        open: true,
        message: "Upload failed. Please try again.",
        severity: "error",
      });
      setLoader(false);
    }
  };

  const fetchEmployeeDetails = async (oracleIds) => {
    const res = await getNoRosterOracleIdDetails(oracleIds);
    return res?.Data || res?.data || [];
  };

  const proceedWithInsert = async (
    oracleIds,
    existingDuplicates = [],
    incomingDuplicates = null,
    incomingCancelled = null,
  ) => {
    setLoader(true);
    const activeDuplicates =
      incomingDuplicates !== null ? incomingDuplicates : duplicateInRecords;
    const activeCancelled =
      incomingCancelled !== null ? incomingCancelled : cancelledScheduleIds;
    const allIdsToFetch = [
      ...new Set([
        ...oracleIds,
        ...activeDuplicates.map((r) => r.OracleId),
        ...activeCancelled.map((r) => r.OracleId),
      ]),
    ];
    try {
      const employeeDetails = await fetchEmployeeDetails(allIdsToFetch);
      const termed = employeeDetails.filter((e) => {
        const s = (e.EmployeeStatus || e.employeeStatus || "")
          .toLowerCase()
          .trim();
        return s !== "" && TERMED_STATUSES.includes(s);
      });
      const tempInactive = employeeDetails.filter((e) =>
        isTempInactive(e.EmployeeStatus || e.employeeStatus),
      );
      const returnedIds = employeeDetails.map((e) => e.OracleId || e.oracleId);
      const missingIds = oracleIds.filter((id) => !returnedIds.includes(id));

      const hasTermed = termed.length > 0;
      const hasTempInactive = tempInactive.length > 0;
      const hasMissing = missingIds.length > 0;
      const hasDuplicates = activeDuplicates.length > 0;
      const hasCancelled = activeCancelled.length > 0;

      if (
        hasTermed ||
        hasTempInactive ||
        hasMissing ||
        hasDuplicates ||
        hasCancelled
      ) {
        if (hasTermed) setTerminatedEmployees(termed);
        if (hasTempInactive) {
          setTempInactiveEmployees(tempInactive);
          setSelectedTempInactiveIds(
            new Set(tempInactive.map((e) => e.OracleId || e.oracleId)),
          );
        }
        // Missing IDs are silently skipped — they have no employee record returned
        const statusMap = {};
        employeeDetails.forEach((e) => {
          statusMap[e.OracleId || e.oracleId] =
            e.EmployeeStatus || e.employeeStatus;
        });

        const termedOracleIds = termed.map((e) => e.OracleId || e.oracleId);
        const tempInactiveIds = tempInactive.map(
          (e) => e.OracleId || e.oracleId,
        );
        const classNameMap = {};
        [...activeDuplicates, ...activeCancelled].forEach((r) => {
          classNameMap[r.OracleId] = r.ClassName;
        });

        if (hasTempInactive) {
          setTempInactiveEmployees(
            tempInactive.map((e) => ({
              ...e,
              CurrentClassName: classNameMap[e.OracleId || e.oracleId] || null,
            })),
          );
          setSelectedTempInactiveIds(
            new Set(tempInactive.map((e) => e.OracleId || e.oracleId)),
          );
        }

        if (hasDuplicates) {
          const enriched = activeDuplicates
            .map((r) => ({
              ...r,
              EmployeeStatus: statusMap[r.OracleId] || null,
            }))
            .filter(
              (r) =>
                !termedOracleIds.includes(r.OracleId) &&
                !tempInactiveIds.includes(r.OracleId),
            );
          setDuplicateInRecords(enriched);
          setSelectedDuplicateIds(new Set(enriched.map((r) => r.OracleId)));
        }
        if (hasCancelled) {
          const enriched = activeCancelled
            .map((r) => ({
              ...r,
              EmployeeStatus: statusMap[r.OracleId] || null,
            }))
            .filter(
              (r) =>
                !termedOracleIds.includes(r.OracleId) &&
                !tempInactiveIds.includes(r.OracleId),
            );
          setCancelledScheduleIds(enriched);
          setSelectedScheduledIds(new Set(enriched.map((r) => r.OracleId)));
        }

        const dupeOracleIds = activeDuplicates.map((r) => r.OracleId);
        const cancelledOracleIds = activeCancelled.map((r) => r.OracleId);
        const activeEmps = employeeDetails.filter((e) => {
          const id = e.OracleId || e.oracleId;
          return (
            !termedOracleIds.includes(id) &&
            !tempInactiveIds.includes(id) &&
            !dupeOracleIds.includes(id) &&
            !cancelledOracleIds.includes(id)
          );
        });
        setActiveEmployees(activeEmps);
        setSelectedActiveIds(
          new Set(activeEmps.map((e) => e.OracleId || e.oracleId)),
        );

        pendingValidIdsRef.current = oracleIds;
        setValidIds(
          oracleIds.filter(
            (id) =>
              !termedOracleIds.includes(id) &&
              !tempInactiveIds.includes(id) &&
              !missingIds.includes(id) &&
              !dupeOracleIds.includes(id) &&
              !cancelledOracleIds.includes(id),
          ),
        );

        setLoader(false);
        setModalState(MODAL_STATE.VALIDATION_SUMMARY);
        return;
      }
      await saveEmployees(employeeDetails, existingDuplicates);
    } catch {
      setSnack({
        open: true,
        message: "Upload failed. Please try again.",
        severity: "error",
      });
      setLoader(false);
    }
  };

  const saveEmployees = async (employeeDetails, existingDuplicates = []) => {
    try {
      const existingClassMap = {};
      existingDuplicates.forEach((r) => {
        existingClassMap[r.OracleId] = r.ClassName;
      });

      const payload = employeeDetails.map((e) => {
        const oracleId = e.OracleId || e.oracleId;
        return {
          omtClassDetailsId: null,
          oracleId,
          classRosterId: null,
          classTraineeId: null,
          noRosterIdClassName: existingClassMap[oracleId] || null,
          isExcelUpload: true,
          omtTechSolutionId: null,
          omtBuildId: null,
          omtTaskTypeId: null,
          omtFinalStatusId: null,
          asdLead: null,
          scheduleStart: null,
          scheduleEnd: null,
          omtScheduleStatusId: null,
          scheduleFailureReason: null,
          ext: false,
          extScheduleStart: null,
          extScheduleEnd: null,
          omtEXTScheduleStatusId: null,
          extScheduleFailureReason: null,
          perr: false,
          perrScheduleStart: null,
          perrScheduleEnd: null,
          omtPERRScheduleStatusId: null,
          perrScheduleFailureReason: null,
          recovery: false,
          recoveryScheduleStart: null,
          recoveryScheduleEnd: null,
          omtRecoveryScheduleStatusId: null,
          recoveryScheduleFailureReason: null,
          comments: null,
          modifiedBy: localStorage.getItem("User"),
        };
      });

      const res = await saveOMTClassDetails(payload);
      const resultRows = Array.isArray(res?.data) ? res.data : [res?.data];

      const successRows = resultRows.filter((r) => {
        const isError = r?.IsError ?? r?.isError;
        return (
          isError === false ||
          isError === 0 ||
          String(isError).toLowerCase() === "false" ||
          parseInt(isError) === 0 ||
          (r?.MessageType || r?.messageType || "").toUpperCase() ===
            "SUCCESS" ||
          (r?.Message || r?.message || "")
            .toLowerCase()
            .includes("saved successfully")
        );
      });
      const failedRows = resultRows.filter((r) => {
        const isError = r?.IsError ?? r?.isError;
        return (
          isError === true ||
          isError === 1 ||
          String(isError).toLowerCase() === "true" ||
          parseInt(isError) === 1
        );
      });
      const actualSavedCount =
        successRows.length === 1 &&
        (successRows[0]?.Message || successRows[0]?.message || "")
          .toLowerCase()
          .includes("saved successfully")
          ? employeeDetails.length
          : successRows.length;
      const isSuccess = successRows.length > 0;

      if (isSuccess) {
        const savedClassName =
          successRows[0]?.ClassName ||
          successRows[0]?.className ||
          "OMT - no class roster";
        const activeCount = employeeDetails.filter((e) => {
          const s = (e.EmployeeStatus || e.employeeStatus || "")
            .toLowerCase()
            .trim();
          return ACTIVE_STATUSES.includes(s);
        }).length;
        setSuccessInfo({
          className: savedClassName,
          totalTrainees: actualSavedCount,
          activeCount,
          termedCount: terminatedEmployees.length,
          savedCount: actualSavedCount,
          skippedCount: failedRows.length,
        });
        if (failedRows.length > 0) {
          setSnack({
            open: true,
            message: `${failedRows.length} Oracle ID${failedRows.length > 1 ? "s" : ""} already exist in another class and were skipped.`,
            severity: "warning",
          });
        }
        setModalState(MODAL_STATE.SUCCESS);
        setSnack({
          open: true,
          message: `${actualSavedCount} record${actualSavedCount !== 1 ? "s" : ""} uploaded successfully to ${savedClassName}.`,
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message:
            "Save failed. All records already exist in another class. Please try again.",
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: "Upload failed. Please try again.",
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const handleViewClass = () => {
    const cls = successInfo?.className;
    handleClose();
    onUploadSuccess(cls);
  };

  const toggleScheduled = (id) => {
    setSelectedScheduledIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllScheduled = (checked) => {
    setSelectedScheduledIds(
      checked
        ? new Set(cancelledScheduleIds.map((r) => r.OracleId))
        : new Set(),
    );
  };
  const toggleAllTempInactive = (checked) => {
    setSelectedTempInactiveIds(
      checked
        ? new Set(tempInactiveEmployees.map((e) => e.OracleId || e.oracleId))
        : new Set(),
    );
  };
  const toggleDuplicate = (id) => {
    setSelectedDuplicateIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllDuplicates = (checked) => {
    setSelectedDuplicateIds(
      checked ? new Set(duplicateInRecords.map((r) => r.OracleId)) : new Set(),
    );
  };
  const toggleActive = (id) => {
    setSelectedActiveIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllActive = (checked) => {
    setSelectedActiveIds(
      checked
        ? new Set(activeEmployees.map((e) => e.OracleId || e.oracleId))
        : new Set(),
    );
  };
  const toggleTempInactive = (id) => {
    setSelectedTempInactiveIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const renderInitial = () => (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#f5f7fa",
          border: "0.5px solid #e0e4ea",
          borderRadius: "8px",
          p: "8px 12px",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 14, verticalAlign: "-2px", mr: 0.5 }}
            />
            OMT_Upload_Template.xlsx
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>
            Required column: Oracle ID
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleDownloadTemplate}
          sx={{ fontSize: "11px", textTransform: "none" }}
        >
          Download template
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: "12px", fontWeight: 500, color: "#374151", mb: 0.5 }}
        >
          Upload file <span style={{ color: "#d32f2f" }}>*</span>
        </Typography>
        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (!file) return;
            const ext = file.name.split(".").pop().toLowerCase();
            if (!["xlsx", "xls"].includes(ext)) {
              setFileError("Please upload a valid .xlsx or .xls file.");
              setSelectedFile(null);
              return;
            }
            setFileError("");
            setSelectedFile(file);
          }}
          sx={{
            border: `1.5px dashed ${fileError ? "#d32f2f" : "#cbd5e1"}`,
            borderRadius: "8px",
            p: "20px",
            textAlign: "center",
            cursor: "pointer",
            background: fileError ? "#fff5f5" : "#f8fafc",
            "&:hover": { borderColor: "#1976d2", background: "#f0f7ff" },
          }}
        >
          <FileUploadOutlinedIcon
            sx={{ fontSize: 30, color: "#9ca3af", mb: 0.5 }}
          />
          <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
            {selectedFile
              ? selectedFile.name
              : "Click to browse or drag and drop your Excel file"}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.5 }}>
            .xlsx or .xls only
          </Typography>
        </Box>
        {fileError && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#d32f2f",
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 13 }} />
            {fileError}
          </Typography>
        )}
        <input
          key={fileKey}
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>

      {selectedFile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#f0f7ff",
            borderRadius: "8px",
            p: "8px 12px",
            border: "0.5px solid #b5d4f4",
          }}
        >
          <InsertDriveFileOutlinedIcon
            sx={{ fontSize: 18, color: "#1976d2" }}
          />
          <Typography sx={{ fontSize: "12px", color: "#0c447c" }}>
            {selectedFile.name}
          </Typography>
        </Box>
      )}
    </>
  );

  const renderUnidentifiedIds = () => (
    <>
      <FilePill name={selectedFile?.name} rowCount={parsedOracleIds.length} />
      <AlertBox type="warning">
        <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
          Unidentified Oracle IDs found in the file.
        </Typography>
        <Typography sx={{ fontSize: "12px", mt: 0.5 }}>
          IDs: {unidentifiedIds.join(", ")}
        </Typography>
        <Typography sx={{ fontSize: "11px", mt: 1 }}>
          You can skip these IDs and continue uploading the valid records, or
          re-upload the corrected file.
        </Typography>
      </AlertBox>
    </>
  );

  const renderValidationSummary = () => {
    const tempSelCount = selectedTempInactiveIds.size;
    const dupSelCount = selectedDuplicateIds.size;
    const schedSelCount = selectedScheduledIds.size;
    const activeSelCount = selectedActiveIds.size;
    const totalSelectable =
      duplicateInRecords.length +
      cancelledScheduleIds.length +
      tempInactiveEmployees.length +
      activeEmployees.length;
    const totalSelected =
      dupSelCount + schedSelCount + tempSelCount + activeSelCount;
    const allSelectableChecked =
      totalSelected === totalSelectable && totalSelectable > 0;
    const someSelectable = totalSelectable > 0;
    const finalUploadCount =
      dupSelCount + schedSelCount + tempSelCount + activeSelCount;
    const termedRows = terminatedEmployees.map((e) => ({
      type: "termed",
      oracleId: e.OracleId || e.oracleId,
      currentClass: "—",
      selectable: false,
      checked: false,
      employeeStatus:
        e.EmployeeStatus || e.employeeStatus || "Terminate Assignment",
    }));

    const tempInactiveRows = tempInactiveEmployees.map((e) => ({
      type: "tempInactive",
      oracleId: e.OracleId || e.oracleId,
      currentClass: e.CurrentClassName || "—",
      selectable: true,
      checked: selectedTempInactiveIds.has(e.OracleId || e.oracleId),
      toggle: () => toggleTempInactive(e.OracleId || e.oracleId),
      employeeStatus: e.EmployeeStatus || e.employeeStatus,
    }));

    const dupRows = duplicateInRecords.map((r) => ({
      type: "duplicate",
      oracleId: r.OracleId,
      currentClass: r.ClassName,
      selectable: true,
      checked: selectedDuplicateIds.has(r.OracleId),
      toggle: () => toggleDuplicate(r.OracleId),
      employeeStatus:
        r.EmployeeStatus || r.employeeStatus || "Active Assignment",
    }));

    const schedRows = cancelledScheduleIds.map((r) => ({
      type: "scheduled",
      oracleId: r.OracleId,
      currentClass: r.ClassName,
      selectable: true,
      checked: selectedScheduledIds.has(r.OracleId),
      toggle: () => toggleScheduled(r.OracleId),
      employeeStatus:
        r.EmployeeStatus || r.employeeStatus || "Active Assignment",
    }));

    const activeRows = activeEmployees.map((e) => ({
      type: "active",
      oracleId: e.OracleId || e.oracleId,
      currentClass: "—",
      selectable: true,
      checked: selectedActiveIds.has(e.OracleId || e.oracleId),
      toggle: () => toggleActive(e.OracleId || e.oracleId),
      employeeStatus:
        e.EmployeeStatus || e.employeeStatus || "Active Assignment",
    }));

    const allRows = [
      ...termedRows,
      ...tempInactiveRows,
      ...dupRows,
      ...schedRows,
      ...activeRows,
    ];

    const handleSelectAll = (checked) => {
      toggleAllDuplicates(checked);
      toggleAllScheduled(checked);
      toggleAllTempInactive(checked);
      toggleAllActive(checked);
    };

    return (
      <>
        <FilePill name={selectedFile?.name} rowCount={parsedOracleIds.length} />

        <AlertBox type="warning">
          <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
            Review required before uploading
          </Typography>
          <Typography sx={{ fontSize: "11px", mt: 0.5 }}>
            {[
              terminatedEmployees.length > 0 &&
                `${terminatedEmployees.length} terminated (excluded)`,
              tempInactiveEmployees.length > 0 &&
                `${tempInactiveEmployees.length} temp inactive (review before upload)`,
              duplicateInRecords.length > 0 &&
                `${duplicateInRecords.length} already in a roster class`,
              cancelledScheduleIds.length > 0 &&
                `${cancelledScheduleIds.length} with cancelled schedule`,
              validIds.length > 0 &&
                `${validIds.length} active (auto-included)`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </AlertBox>

        <Box sx={{ ...TABLE_SX, maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={TH_SX} padding="checkbox">
                  {someSelectable && (
                    <Checkbox
                      size="small"
                      sx={{ p: "2px" }}
                      checked={allSelectableChecked}
                      indeterminate={
                        (dupSelCount > 0 ||
                          schedSelCount > 0 ||
                          tempSelCount > 0) &&
                        !allSelectableChecked
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  )}
                </TableCell>
                <TableCell sx={TH_SX}>Oracle ID</TableCell>
                <TableCell sx={TH_SX}>Current Class</TableCell>
                <TableCell sx={TH_SX}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allRows.map((row, idx) => (
                <TableRow
                  key={`${row.type}-${row.oracleId}-${idx}`}
                  sx={{
                    opacity: row.type === "termed" ? 0.5 : 1,
                    background:
                      row.type === "termed"
                        ? "#fafafa"
                        : row.type === "active"
                          ? "#f0fdf4"
                          : row.checked
                            ? "#eff6ff"
                            : "inherit",
                  }}
                >
                  <TableCell sx={TD_SX} padding="checkbox">
                    <Checkbox
                      size="small"
                      sx={{ p: "2px" }}
                      checked={row.checked}
                      disabled={!row.selectable}
                      onChange={row.selectable ? row.toggle : undefined}
                    />
                  </TableCell>
                  <TableCell sx={TD_SX}>{row.oracleId}</TableCell>
                  <TableCell
                    sx={{ ...TD_SX, color: "#6b7280", fontSize: "11px" }}
                  >
                    {row.currentClass}
                  </TableCell>
                  <TableCell sx={TD_SX}>
                    {getStatusChip(row.employeeStatus)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {someSelectable && (
          <SubNote>
            {dupSelCount + schedSelCount + tempSelCount + activeSelCount} of{" "}
            {duplicateInRecords.length +
              cancelledScheduleIds.length +
              tempInactiveEmployees.length +
              activeEmployees.length}{" "}
            selected to move
          </SubNote>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            background: "#f0f7ff",
            border: "0.5px solid #b5d4f4",
            borderRadius: "8px",
            p: "8px 14px",
            mt: 1,
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "#0c447c" }}>
            <strong>{finalUploadCount}</strong> Oracle ID
            {finalUploadCount !== 1 ? "s" : ""} will be uploaded
          </Typography>
        </Box>
      </>
    );
  };

  const renderSuccess = () => (
    <Box sx={{ textAlign: "center", py: 2 }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          background: "#f0fdf4",
          border: "2px solid #86efac",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 12px",
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 28, color: "#2e7d32" }} />
      </Box>
      <Typography sx={{ fontSize: "15px", fontWeight: 500, mb: 0.5 }}>
        Class created successfully
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "#6b7280", mb: 2 }}>
        {successInfo?.className}
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
        {[
          { label: "Uploaded", value: successInfo?.savedCount },
          { label: "Active", value: successInfo?.activeCount },
          ...(successInfo?.skippedCount > 0
            ? [{ label: "Skipped", value: successInfo?.skippedCount }]
            : []),
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              flex: 1,
              background: "#f5f7fa",
              borderRadius: "8px",
              p: "10px",
            }}
          >
            <Typography sx={{ fontSize: "22px", fontWeight: 500 }}>
              {stat.value}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#6b7280", mt: 0.5 }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <AlertBox type="success">
        <Typography sx={{ fontSize: "12px" }}>
          Employment Status, Client, Program, and Project have been
          auto-populated for all trainees. The class is now available in OMT
          Class Schedule.
        </Typography>
      </AlertBox>

      {successInfo?.termedCount > 0 && (
        <AlertBox type="info">
          <Typography sx={{ fontSize: "12px" }}>
            {successInfo.termedCount} terminated employee
            {successInfo.termedCount > 1 ? "s were" : " was"} automatically
            excluded from this upload.
          </Typography>
        </AlertBox>
      )}
    </Box>
  );

  const renderFooter = () => {
    const btnSx = { textTransform: "none", fontSize: "13px" };
    switch (modalState) {
      case MODAL_STATE.INITIAL:
        return (
          <>
            <Button variant="outlined" onClick={handleClose} sx={btnSx}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleUpload} sx={btnSx}>
              Upload
            </Button>
          </>
        );
      case MODAL_STATE.UNIDENTIFIED_IDS:
        return (
          <>
            <Button variant="outlined" onClick={handleClose} sx={btnSx}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setModalState(MODAL_STATE.INITIAL);
                setSelectedFile(null);
                setFileKey((k) => k + 1);
                setUnidentifiedIds([]);
              }}
              sx={btnSx}
            >
              Re-upload file
            </Button>
            <Button
              variant="contained"
              onClick={handleSkipAndContinue}
              sx={btnSx}
            >
              Skip &amp; continue
            </Button>
          </>
        );
      case MODAL_STATE.VALIDATION_SUMMARY: {
        const finalCount =
          selectedDuplicateIds.size +
          selectedScheduledIds.size +
          selectedTempInactiveIds.size +
          selectedActiveIds.size;
        return (
          <>
            <Button variant="outlined" onClick={handleClose} sx={btnSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleValidationSummaryConfirm}
              disabled={finalCount === 0}
              sx={btnSx}
            >
              Upload {finalCount} record{finalCount !== 1 ? "s" : ""}
            </Button>
          </>
        );
      }
      case MODAL_STATE.SUCCESS:
        return (
          <Button
            variant="contained"
            color="success"
            onClick={handleViewClass}
            sx={btnSx}
          >
            View class in OMT
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "0.5px solid #e0e4ea",
            minHeight: "560px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "0.5px solid #e0e4ea",
            py: 1.5,
            px: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FileUploadOutlinedIcon sx={{ fontSize: 18, color: "#1976d2" }} />
            <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
              Upload class
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, px: 3, pb: 1 }}>
          {loader && <LinearProgress sx={{ mb: 1.5 }} />}
          {modalState === MODAL_STATE.INITIAL && renderInitial()}
          {modalState === MODAL_STATE.UNIDENTIFIED_IDS &&
            renderUnidentifiedIds()}
          {modalState === MODAL_STATE.VALIDATION_SUMMARY &&
            renderValidationSummary()}
          {modalState === MODAL_STATE.SUCCESS && renderSuccess()}
        </DialogContent>

        <DialogActions
          sx={{ borderTop: "0.5px solid #e0e4ea", px: 2.5, py: 1.5, gap: 1 }}
        >
          {renderFooter()}
        </DialogActions>
      </Dialog>

      <SnackBar
        snack={snack}
        snackReset={() => setSnack({ open: false, message: "", severity: "" })}
      />
    </>
  );
};

UploadClassModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
};

export default UploadClassModal;
